import { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import { Loader } from "components/Loader";
import { togglePurchasePlanModal } from "Layout/slice";
import { useFaqs } from "features/HelpCenter/hook";
import { SettingsBackChevronIcon, SettingsFaqChevronIcon } from "assets/icons";
import { FaqContainer } from "./FaqPanel.styles";

// The FAQ list inside the Settings modal: the same questions and answers the
// Help Center shows (they come from the backend), grouped by section.
export const FaqPanel = ({ onBack }) => {
  const dispatch = useDispatch();
  const { isLoading, faqs, groupedFaqs } = useFaqs();
  const [openKeys, setOpenKeys] = useState(() => new Set());

  const toggle = (key) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Answers are backend HTML; a "here" link in them opens the pricing modal.
  const handleAnswerClick = (event) => {
    if (event.target.closest('[data-faq-action="open-pricing"]')) {
      event.preventDefault();
      dispatch(togglePurchasePlanModal(true));
    }
  };

  return (
    <FaqContainer>
      <button type="button" className="back-button" onClick={onBack}>
        <SettingsBackChevronIcon aria-hidden="true" />
        Support
      </button>
      <h3 className="panel-heading">FAQs</h3>
      <p className="panel-subtitle">
        Find quick answers to common questions about Surmount.
      </p>

      {isLoading && <Loader />}
      {!isLoading && faqs.length === 0 && (
        <div className="faq-empty">No FAQs found.</div>
      )}

      <div className="faq-groups">
        {groupedFaqs.map((group, groupIndex) => (
          <div className="faq-group" key={group.section}>
            <p className="faq-section">{group.section}</p>
            {group.items.map((item, itemIndex) => {
              const key = `${groupIndex}-${itemIndex}`;
              const isOpen = openKeys.has(key);
              return (
                <div className={`faq-item${isOpen ? " open" : ""}`} key={key}>
                  <button
                    type="button"
                    className="faq-question"
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${key}`}
                    onClick={() => toggle(key)}
                  >
                    {item.title}
                    <SettingsFaqChevronIcon
                      className="faq-chevron"
                      aria-hidden="true"
                    />
                  </button>
                  {isOpen && (
                    <div
                      id={`faq-answer-${key}`}
                      className="faq-answer"
                      onClick={handleAnswerClick}
                    >
                      {item.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </FaqContainer>
  );
};

FaqPanel.propTypes = {
  onBack: PropTypes.func.isRequired,
};
